import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAssessmentInfo, putAssessmentInfo } from "../../../redux/actions/assessmentInfo/assessmentInfo";
import { ProfileOverviewState } from "../../../redux/reducers/getProfile/getProfileOverviewReducer";
import { SaveDataState } from "../../../redux/reducers/getSaveData/saveDataReducer";
import { SaveResultState } from "../../../redux/reducers/getSaveData/saveResultReducer";
import { AggregateSiteAssessmentInfoState } from "../../../types/assessment_info/AggregateSiteAssessmentInfoState";
import { defaultAggregateSiteAssessmentInfo, AggregateSiteAssessmentInfo, defaultAggregateSiteAnalysisTypes, AggregateSiteLandAttributeSchema, AggregateOptionWithFlag } from "../../../types/assessment_info/aggregateSites/AggregateSiteData";
import { selectELSLookups, selectGetAggregateSiteAssessmentInfo, selectProfileGetOverview, selectSaveData, selectSaveResult } from "../../../redux/selectors/exportSelectorPaths";
import { GetElsLookupsState } from "../../../redux/reducers/getLookups/getElsLookupsReducer";
import { getElsLookups } from "../../../redux/actions/lookups/lookups";
import NumberInput from "../../common/NumberInput/NumberInput";
import Dropdown, { Option } from "../../common/Dropdown/Dropdown";
import RadioButton from "../../common/RadioGroup/RadioButton";
import { useTranslation } from "react-i18next";
import "../UnlicensedLands/UnlicensedLands.css";
import { exactMatcher, getFormErrorByName, getFormErrorMessageByName, hasError, useFormErrorContext } from "../../../utils/context/FormErrorContext";
import { convertToAcres } from "../../../utils/convertUomToImperial";
import i18n from "../../../i18n";
import { useNavigation } from "../../../utils/context/NavigationContext";
import ErrorMessages from "../../common/ErrorMessages/ErrorMessages";
import { toast } from "react-toastify";
import ConfirmationDialog from "../../common/Modal/ConfirmationDialog";

export default function UnlicensedLands(): JSX.Element {
  const dispatch = useDispatch();
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
  const assessmentInfoState: AggregateSiteAssessmentInfoState = useSelector(selectGetAggregateSiteAssessmentInfo)
  const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
  const currentSnapshotId = sessionStorage.getItem('currentSnapshotId') as string;
  const [isUpdateReqSent, setIsUpdateReqSent] = useState(false);
  const saveData: SaveDataState = useSelector(selectSaveData)
  const saveResultState: SaveResultState = useSelector(selectSaveResult);
  const { t } = useTranslation("unlicensed_lands")
  const analysisFlagOptions = (elsLookupsState.dropdowns && elsLookupsState.dropdowns["rentAnalysisOptions"] as Option[]) ?? []
  const unitTypeOptions = (elsLookupsState.dropdowns && elsLookupsState.dropdowns["areaUomOptions"] as Option[]) ?? []
  const [assessmentInfo, setAssessmentInfo] = useState(defaultAggregateSiteAssessmentInfo);
  const profileType = profileOverview.profile.profile_type_code;
  const isInternalUser = sessionStorage.getItem("isInternalUser") ? true : false;
  const { errors, removeError, clearErrors } = useFormErrorContext();
  const { direction } = useNavigation()
  const [dataError, setDataError] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [tempUom, setTempUom] = useState<string | null>(null)

  useEffect(() => {
    let data = { ...assessmentInfo }
    if (Object.keys(assessmentInfoState.formData).length > 0) {
      data = { ...assessmentInfoState.formData as AggregateSiteAssessmentInfo }
    }
    if (!data["analysisTypes"] || data["analysisTypes"]?.length === 0) {
      data["analysisTypes"] = defaultAggregateSiteAnalysisTypes
    }
    data["analysisTypes"].forEach(e => e.componentId = String(crypto.randomUUID()))
    setAssessmentInfo(data)
    setTempUom(data.land?.unlicensedLandUom as string)
    // eslint-disable-next-line
  }, [assessmentInfoState, elsLookupsState])

  useEffect(() => {
    if (!elsLookupsState.dropdowns) {
      const propertyCode = sessionStorage.getItem("propertyCode") ?? "";
      dispatch(getElsLookups(propertyCode))
    }
  }, [elsLookupsState.dropdowns, dispatch]);

  useEffect(() => {
    if (currentSnapshotId && profileOverview.profile.profile_type_code && !assessmentInfoState.loading
      && (!assessmentInfoState.success || currentSnapshotId !== assessmentInfoState.snapshot_id)) {
      if (!dataError) {
        dispatch(getAssessmentInfo(currentSnapshotId, profileOverview.profile.profile_type_code))
        if (!assessmentInfoState.success && assessmentInfoState.error) {
          toast.error(assessmentInfoState.message || "Failed to find assessment info with snapshot id");
          setDataError(true)
        }
      }
    }
  }, [assessmentInfoState, currentSnapshotId, dispatch, profileOverview.profile.profile_type_code, dataError]);


  useEffect(() => {
    if (saveData.saving) {
      clearErrors()
      const triggerSpectral = isInternalUser || direction !== "backward"
      let spectralSection = null
      if (profileType === "AGS") {
        spectralSection = "ags-unlicensed-lands"
      }
      const data = {
        licenseOperationTypeCode: assessmentInfo["licenseOperationTypeCode"],
        licenseActiveFlag: assessmentInfo["licenseActiveFlag"],
        licenses: assessmentInfo["licenses"],
        surrenderRelinquishLicenseFlag: assessmentInfo["surrenderRelinquishLicenseFlag"],
        surrenderRelinquishLicenseDate: assessmentInfo["surrenderRelinquishLicenseDate"],
        rehabilitatedLicensedLandFlag: assessmentInfo["rehabilitatedLicensedLandFlag"],
        unlicensedLandFlag: assessmentInfo["unlicensedLandFlag"],
        licensedPondFlag: assessmentInfo["licensedPondFlag"],
        belowWaterExtractionFlag: assessmentInfo["belowWaterExtractionFlag"],
        attachments: assessmentInfo["attachments"],
        land: assessmentInfo["land"],
        pond: assessmentInfo["pond"],
        analysisTypes: assessmentInfo["analysisTypes"],
        licensedLands1: assessmentInfo["licensedLands1"],
        licensedLands2: assessmentInfo["licensedLands2"],
        licensedLands3: assessmentInfo["licensedLands3"],
        unlicensedLands: assessmentInfo["unlicensedLandFlag"] !== true ? [] : assessmentInfo["unlicensedLands"]
      }
      dispatch(putAssessmentInfo(currentSnapshotId, {
        "data": { ...data },
        "spectral_section": spectralSection,
        "trigger_spectral": triggerSpectral,
        "profile_type_code": "AGS",
        "is_internal": isInternalUser
      }));
      setIsUpdateReqSent(true);
    }
  }, [currentSnapshotId, dispatch, saveData.saving, assessmentInfo, profileType, clearErrors, direction, isInternalUser]);

  useEffect(() => {
    if (isUpdateReqSent && !saveResultState.loading) {
      if (saveResultState.status === 200) {
        dispatch(getAssessmentInfo(currentSnapshotId, profileOverview.profile.profile_type_code));
      }
      setIsUpdateReqSent(false);
    }
  }, [currentSnapshotId, dispatch, isUpdateReqSent, profileOverview.profile.profile_type_code, saveResultState]);

  const getDescription = (code: string) => {
    if (elsLookupsState.dropdowns?.aggregateLandType) {
      return (elsLookupsState.dropdowns?.aggregateLandType as AggregateOptionWithFlag[])?.find(e => e.code === code)?.description?.trim() || ""
    }
    return ""
  }

  const getDefinition = (code: string) => {
    if (elsLookupsState.dropdowns?.aggregateLandType) {
      return (elsLookupsState.dropdowns?.aggregateLandType as AggregateOptionWithFlag[])?.find(e => e.code === code)?.description?.trim() + "Definition" || ""
    }
    return ""
  }

  const convertUom = (uom: string) => {
    const unitType = unitTypeOptions.find(o => o.value === uom)
    return i18n.language === 'fr' ? unitType?.frenchText : unitType?.text
  }

  const handleUomChange = (uom: string) => {
    const unitType = unitTypeOptions.find(o => o.value === uom)
    if (unitType) {
      const land = assessmentInfo.land
      if (land) {
        land.unlicensedLandUom = uom
      }
      const unlicensedLands = assessmentInfo.unlicensedLands
      unlicensedLands?.forEach(e => {
        e.area = null
        e.areaSys = null
      })
      setAssessmentInfo({ ...assessmentInfo, land: land, unlicensedLands: unlicensedLands })
    }
    removeError(getFormErrorByName(errors, "assessmentInfo.land.unlicensedLandUom")?.primaryKey as string)
  }

  return (
    <>
      <div>
        <ErrorMessages errors={errors} />
        <div className="margin-bottom-1">{t("unlicensedLandDefinition")}</div>
        <div className="inline-block margin-right-1">{t("hasUnlicensedLands1")} <u>{t("unlicensed")}</u> {t("hasUnlicensedLands2")}</div>
        <RadioButton name="unlicensed-lands" id="unlicensed-lands-yes" text={t("yes")} textPosition='right' value="true"
          currentValue={assessmentInfo.unlicensedLandFlag || ""} onChange={() => {
            setAssessmentInfo({ ...assessmentInfo, unlicensedLandFlag: true })
            removeError(getFormErrorByName(errors, "unlicensedLandFlag")?.primaryKey as string)
          }} className="inline-block margin-right-1"
          isError={hasError(errors, "unlicensedLandFlag")}
          ariaLabel="unlicensed-lands" />
        <RadioButton name="unlicensed-lands" id="unlicensed-lands-no" text={t("no")} textPosition='right' value="false"
          currentValue={assessmentInfo.unlicensedLandFlag === false ? "false" : ""} onChange={() => {
            setAssessmentInfo({ ...assessmentInfo, unlicensedLandFlag: false, unlicensedLands: [] })
            removeError(getFormErrorByName(errors, "unlicensedLandFlag")?.primaryKey as string)
          }} className="inline-block"
          isError={hasError(errors, "unlicensedLandFlag")}
          ariaLabel="unlicensed-lands" />
      </div>
      {assessmentInfo.unlicensedLandFlag ?
        <div className="width-100">
          <span className="inline-block ags-input-label">{t("identifyUom")}</span>
          <span className="inline-block"><Dropdown options={unitTypeOptions}
            onChange={(e) => {
              if (!tempUom) {
                handleUomChange((e.target as HTMLSelectElement).value)
              } else {
                setShowConfirmation(true)
              }
              setTempUom((e.target as HTMLSelectElement).value)
            }}
            name="unit-type-options" ariaLabel="Unit type options" value={assessmentInfo.land?.unlicensedLandUom || ""} placeholder={true}
            hideErrorCross
            errorMessage={getFormErrorMessageByName(errors, "assessmentInfo.land.unlicensedLandUom", exactMatcher)}
            className={"uom-dropdown" + (hasError(errors, "assessmentInfo.land.unlicensedLandUom", exactMatcher) ? " ags-table-error" : "")} /></span>
          <table className="wizard-table unlicensed-lands-table" role="presentation">
            <thead>
              <tr>
                <th className="unlicensed-lands-table-heading-1">{t("unlicensedLand")}</th>
                <th className="unlicensed-lands-table-heading-2">{t("area")} {assessmentInfo.land?.unlicensedLandUom ? "(" + convertUom(assessmentInfo.land?.unlicensedLandUom as string) + ")" : null}</th>
              </tr>
            </thead>
            <tbody>
              {assessmentInfo.unlicensedLands?.map((e: AggregateSiteLandAttributeSchema, i: number) =>
                <tr key={i}>
                  <td>
                    <div className="bold">{t(getDescription(e.landTypeCode as string))}</div>
                    <div className="margin-top-half-space">{t(getDefinition(e.landTypeCode as string) as string)}</div>
                  </td>
                  <td className={`${hasError(errors, "assessmentInfo.land.landTypes." + e.componentId + ".area", exactMatcher) ? "ags-table-error" : ""}`}><NumberInput name={"assessmentInfo.land.landTypes." + e.componentId + ".area"} border={false} textAlign="center"
                    negatives={false} decimalPlaces={2} commas={true} max={99999999.9}
                    initialValue={e.area !== null ? e.area : null}
                    onChange={(value, _last) => {
                      const land = assessmentInfo.unlicensedLands
                      if (land?.length) {
                        land[i].area = value
                        land[i].areaSys = convertToAcres(assessmentInfo.land?.unlicensedLandUom as string, value || null)
                      }
                      setAssessmentInfo({ ...assessmentInfo, unlicensedLands: land })
                      removeError(getFormErrorByName(errors, "assessmentInfo.land.landTypes." + e.componentId as string + ".area")?.primaryKey as string)
                    }}
                    errorMatcher={exactMatcher}
                  /></td>
                </tr>
              )}
            </tbody>
          </table>
        </div> : <></>}
      {profileType === "AGS" && isInternalUser && sessionStorage.getItem("campaignStatusCode") === "EXTERNAL_IN_PROGRESS" && analysisFlagOptions.length > 0 &&

        <div className='airp-badge badge-lighter-suburb-yellow analysis-container margin-top-1'>
          <label htmlFor='analysisFlag'>{t("analysis")}</label>
          <div className='reviewed-input-container'>
            <div className='reviewed-check-container'>
              <Dropdown id="analysisFlag" name='analysisFlag' className='admin-input-style'
                options={analysisFlagOptions}
                value={assessmentInfo.analysisTypes?.find(e => e.analysisTypeCode === "U")?.analysisFlag}
                onChange={(e) => {
                  const temp = assessmentInfo.analysisTypes
                  if (temp?.length) {
                    const idx = temp?.findIndex(e => e.analysisTypeCode === "U")
                    temp[idx] = {
                      ...temp[idx],
                      "analysisFlag": (e.target as HTMLSelectElement).value,
                      "analysisReviewedBy": localStorage.getItem("username") ?? "SYSTEM",
                      "analysisReviewedDate": (new Date()).toISOString()
                    }
                  }
                  setAssessmentInfo({ ...assessmentInfo, analysisTypes: temp })
                  removeError(getFormErrorByName(errors, assessmentInfo.analysisTypes?.find(e => e.analysisTypeCode === "U")?.componentId as string)?.primaryKey as string)
                }}
                hideErrorCross
                errorMessage={getFormErrorMessageByName(errors, assessmentInfo.analysisTypes?.find(e => e.analysisTypeCode === "U")?.componentId as string, exactMatcher)}
              />
            </div>
          </div>

        </div>
      }
      {showConfirmation &&
        <ConfirmationDialog
          onCancel={() => { setShowConfirmation(false) }}
          onConfirm={() => {
            setShowConfirmation(false)
            handleUomChange(tempUom as string)
          }}
          headerClass="no-border" buttonGroupClass="justify-content-center no-border"
        >
          {t("confirmation")}
        </ConfirmationDialog>
      }
    </>
  )
}

